import { api, isProblemType } from '@most/api-client';
import type { Schemas } from '@most/api-client';
import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { withCategoryIntent } from './intent';
import { LOGIN_PATH } from './origins';

export type Me = Schemas['Me'];
export type Role = Schemas['Role'];
export type UserStatus = Schemas['UserStatus'];
export type Locale = Schemas['Locale'];

export const meQueryKey = ['me'] as const;

/**
 * Every SPA calls this first. It is the source of the role guard's decision, the notification
 * bell badge, and the Telegram chip state (openapi: `GET /me`).
 *
 * A 401 is a NORMAL result here, not an error: it just means "nobody is logged in". Retrying it
 * would be pointless, and letting it bubble to the global handler would redirect a guest who is
 * already sitting on the login page.
 */
export function useMe() {
  return useQuery({
    queryKey: meQueryKey,
    queryFn: async (): Promise<Me | null> => {
      try {
        return await api.get<Me>('/me');
      } catch (e) {
        if (isProblemType(e, 'auth-required')) return null;
        throw e;
      }
    },
    staleTime: 60_000,
    retry: false,
  });
}

export function useLogout() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<void>('/auth/logout'),
    onSuccess: () => {
      queryClient.clear();
    },
  });
}

/**
 * `locale` is the only self-service attribute on `users` (openapi: `PATCH /me`). It also drives
 * the language the outbox renders this user's emails and Telegram messages in — so it is not
 * merely a UI preference.
 */
export function useUpdateLocale() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (locale: Locale) => api.patch<Me>('/me', { locale }),
    onSuccess: (me) => {
      queryClient.setQueryData(meQueryKey, me);
    },
  });
}

/**
 * Wire the api-client's 401 hook into the router and the query cache. Called ONCE, at app boot.
 *
 * Three copies of this would be three different bugs — which is why it lives in the shared
 * package and not in each SPA's main.tsx.
 */
export function installUnauthorizedHandler(
  queryClient: QueryClient,
  redirect: (to: string) => void,
  loginPath = LOGIN_PATH,
): void {
  let redirecting = false;

  api.setUnauthorizedHandler(() => {
    // A burst of parallel queries can 401 together; only redirect once.
    if (redirecting) return;
    redirecting = true;

    queryClient.clear();

    // No `?next=`. The login page routes by role and never read it, and since D16 it is on a
    // different origin — reviving it would mean the landing taking a redirect target from a
    // query string, which is an open redirect unless very carefully validated. A category
    // SLUG does ride along (./intent): the guest who pressed "order" on a landing product card
    // hits this 401 before the guard ever runs, and should come back to that category.
    redirect(
      typeof window === 'undefined' ? loginPath : withCategoryIntent(loginPath, window.location.search),
    );

    // Let a genuinely new navigation trigger a fresh redirect later.
    setTimeout(() => {
      redirecting = false;
    }, 1000);
  });
}
